import { GoatGetBuyBarData200ResponseInner } from "definitions/goat-api";
import { DetailWithPricesResponseTypeV5 } from "definitions/poizon-api";
import { ProductInfoResponseDto } from "definitions/poizon-api-product";
import { ProductSourceDtoSourceEnum } from "definitions/product-storage";
import {
  ProductFullResponse,
  SizePriceGroup,
  SupplierPrice,
} from "definitions/shihuo-api";
import { shihuoStoreLabel } from "src/constants/shihuoStores";
import {
  priceCurrency,
  SIZE_ATTR_NAME,
} from "src/pages/ProductDetailPage/ProductDetailPage.constant";

import { PriceItemDto, VariantTreeDto } from "../VariantTreeDto";

export function buildVariantsProduct(
  product:
    | DetailWithPricesResponseTypeV5
    | ProductInfoResponseDto
    | ProductFullResponse
    | GoatGetBuyBarData200ResponseInner[],
  platform: ProductSourceDtoSourceEnum,
): VariantTreeDto | null {
  if (!product) return null;

  switch (platform) {
    case ProductSourceDtoSourceEnum.Dewu:
      return "skus" in product ? buildVariantsFromDewu(product) : null;

    case ProductSourceDtoSourceEnum.Poizon:
      return "buyDialogModel" in product
        ? buildVariantsFromPoizon(product)
        : null;

    case ProductSourceDtoSourceEnum.Goat:
      return Array.isArray(product) ? buildVariantsFromGoat(product) : null;

    case ProductSourceDtoSourceEnum.Shihuo:
      return !Array.isArray(product) && "sizePrices" in product
        ? buildVariantsFromShihuo(product)
        : null;

    default:
      return null;
  }
}

export function buildVariantsFromDewu(
  product: DetailWithPricesResponseTypeV5,
): VariantTreeDto {
  const attributes: Record<string, Set<string>> = {};
  const variants: VariantTreeDto["variants"] = {};

  for (const sku of product.skus ?? []) {
    const prices =
      sku.price?.prices.map((p) => ({
        price: p.price / 100,
        currency: priceCurrency.CNY,
        tradeType: p.tradeType,
        delivery: p.timeDelivery
          ? {
              min: p.timeDelivery.min ?? null,
              max: p.timeDelivery.max ?? null,
            }
          : undefined,
      })) ?? [];

    if (!prices.length) continue;

    const props =
      sku.properties
        ?.map((p) => p.saleProperty)
        .filter((p): p is NonNullable<typeof p> => Boolean(p)) ?? [];

    if (!props.length) continue;

    for (const prop of props) {
      attributes[prop.name] ??= new Set();
      attributes[prop.name].add(prop.value);
    }

    const path = props.map((p) => `${p.name}=${p.value}`).join("|");

    variants[path] = {
      skuId: sku.skuId,
      prices,
    };
  }

  return {
    attributes: normalizeAttributes(attributes),
    variants,
  };
}

export function buildVariantsFromPoizon(
  product: ProductInfoResponseDto,
): VariantTreeDto {
  const attributes: Record<string, Set<string>> = {};
  const variants: VariantTreeDto["variants"] = {};

  const buyDialog = product.buyDialogModel;
  const skus = buyDialog.skus ?? [];
  const saleProperties = buyDialog.saleProperties ?? [];

  const propertyMap = new Map<number, { name: string; value: string }>();

  for (const prop of saleProperties) {
    for (const propGroup of prop.propertyList ?? []) {
      for (const model of propGroup.propertyItemModels ?? []) {
        propertyMap.set(model.propertyValueId, {
          name: prop.name,
          value: model.value,
        });
      }
    }
  }

  for (const sku of skus) {
    const skuSpeed = sku.skuSpeedInfo?.[0];

    const prices: PriceItemDto[] = [];

    if (skuSpeed?.speedPrice?.money?.amount) {
      prices.push({
        price: Number(skuSpeed.speedPrice.money.amount),
        currency: skuSpeed.speedPrice.money?.currency ?? priceCurrency.RUB,
        delivery: {
          min: skuSpeed.minDay,
          max: skuSpeed.maxDay,
        },
      });
    } else if (sku.minPrice?.money?.minUnitVal) {
      prices.push({
        price: sku.minPrice.money.minUnitVal / 100,
        currency: sku.minPrice.money?.currency ?? priceCurrency.RUB,
      });
    }

    if (!prices.length) continue;

    const props =
      sku.properties
        ?.map((p) => propertyMap.get(p.propertyValueId))
        .filter(Boolean) ?? [];

    if (!props.length) continue;

    for (const prop of props) {
      if (prop) {
        attributes[prop.name] ??= new Set();
        attributes[prop.name].add(prop.value);
      }
    }

    const path = props.map((p) => (p ? `${p.name}=${p.value}` : ``)).join("|");

    variants[path] = {
      skuId: sku.skuId,
      prices,
    };
  }

  return {
    attributes: normalizeAttributes(attributes),
    variants,
  };
}

export function buildVariantsFromGoat(
  items: GoatGetBuyBarData200ResponseInner[],
): VariantTreeDto {
  const attributes: Record<string, Set<string>> = {};
  const variants: VariantTreeDto["variants"] = {};

  for (const item of items) {
    const lowestAmount = item.lowestPriceCents.amount;

    if (lowestAmount == null) continue;

    const sizeLabel = item.sizeOption.presentation;
    const price = lowestAmount / 100;
    const path = `${SIZE_ATTR_NAME}=${sizeLabel}`;

    const existing = variants[path];
    if (existing && existing.prices[0].price <= price) continue;

    attributes[SIZE_ATTR_NAME] ??= new Set();
    attributes[SIZE_ATTR_NAME].add(sizeLabel);

    variants[path] = {
      skuId: item.sizeOption.value,
      prices: [
        {
          price,
          currency: item.lowestPriceCents.currency ?? priceCurrency.USD,
        },
      ],
    };
  }

  return {
    attributes: normalizeAttributes(attributes),
    variants,
  };
}

const UNKNOWN_STORE_LABEL = "—";

function storeLabel(supplier: SupplierPrice): string {
  const store = supplier.store ?? supplier.platform?.name;

  return store ? shihuoStoreLabel(store) : UNKNOWN_STORE_LABEL;
}

function toShihuoPrice(supplier: SupplierPrice): PriceItemDto | null {
  const price = Number(supplier.price ?? supplier.displayPrice);

  if (!Number.isFinite(price) || price <= 0) return null;

  return {
    price,
    currency: priceCurrency.CNY,
    delivery: supplier.delivery && {
      min: supplier.delivery.min ?? null,
      max: supplier.delivery.max ?? null,
      unit: supplier.delivery.unit,
    },
    store: storeLabel(supplier),
    supplierName: supplier.supplierName,
    url: supplier.platform?.url,
  };
}

function lowestPricePerStore(offers: PriceItemDto[]): PriceItemDto[] {
  const byStore = new Map<string, PriceItemDto>();

  for (const offer of offers) {
    const store = offer.store ?? UNKNOWN_STORE_LABEL;
    const known = byStore.get(store);

    if (!known || offer.price < known.price) {
      byStore.set(store, offer);
    }
  }

  return [...byStore.values()].sort((a, b) => a.price - b.price);
}

function cardLowestPrice(group: SizePriceGroup): PriceItemDto[] {
  const price = Number(group.lowestPrice);

  if (!Number.isFinite(price) || price <= 0) return [];

  return [{ price, currency: priceCurrency.CNY }];
}

export function buildVariantsFromShihuo(
  product: ProductFullResponse,
): VariantTreeDto {
  const attributes: Record<string, Set<string>> = {};
  const variants: VariantTreeDto["variants"] = {};

  for (const group of product.sizePrices ?? []) {
    const sizeLabel = group.size;

    if (!sizeLabel) continue;

    const offers = (group.suppliers ?? [])
      .map(toShihuoPrice)
      .filter((offer): offer is PriceItemDto => offer !== null)
      .sort((a, b) => a.price - b.price);

    const prices = offers.length
      ? lowestPricePerStore(offers)
      : cardLowestPrice(group);

    if (!prices.length) continue;

    attributes[SIZE_ATTR_NAME] ??= new Set();
    attributes[SIZE_ATTR_NAME].add(sizeLabel);

    variants[`${SIZE_ATTR_NAME}=${sizeLabel}`] = {
      skuId: Number(group.skuId),
      prices,
      offers,
    };
  }

  return {
    attributes: normalizeAttributes(attributes),
    variants,
  };
}

function normalizeAttributes(
  attributes: Record<string, Set<string>>,
): Record<string, string[]> {
  return Object.fromEntries(
    Object.entries(attributes).map(([attributeName, values]) => [
      attributeName,
      [...values],
    ]),
  );
}
